/**
 * UI Helper Functions
 * Common UI utilities for toasts, modals, and other UI operations
 */

import { i18nService } from '../services/i18n-service.js';

/**
 * Show a toast notification
 */
export function showToast(message: string, type: 'info' | 'success' | 'error' = 'info', duration: number = 3000): void {
  const toast = document.createElement('div');
  toast.className = `toast toast--${type}`;
  toast.textContent = message;
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  
  document.body.appendChild(toast);
  
  // Trigger animation
  requestAnimationFrame(() => {
    toast.classList.add('toast--visible');
  });
  
  // Remove after duration
  setTimeout(() => {
    toast.classList.remove('toast--visible');
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300); // Match CSS transition duration
  }, duration);
}

/**
 * Show a confirmation modal
 */
export function showConfirmModal(
  message: string, 
  onConfirm?: () => void, 
  onCancel?: () => void
): void {
  const modalContainer = document.getElementById('modal-container');
  if (!modalContainer) return;
  
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'confirm-modal-message');
  
  const panel = document.createElement('div');
  panel.className = 'modal-panel modal-panel--small';
  
  const body = document.createElement('div');
  body.className = 'modal-body';
  
  const messageEl = document.createElement('p');
  messageEl.id = 'confirm-modal-message';
  messageEl.textContent = message;
  messageEl.style.cssText = 'margin: 0; font-size: 0.9375rem; color: #374151;';
  body.appendChild(messageEl);
  
  panel.appendChild(body);
  
  const footer = document.createElement('div');
  footer.className = 'modal-footer';
  
  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'modal-btn modal-btn--secondary';
  cancelBtn.textContent = i18nService.getString('cancel') || 'Cancel';
  cancelBtn.addEventListener('click', () => {
    closeConfirmModal();
    if (onCancel) onCancel();
  });
  footer.appendChild(cancelBtn);
  
  const confirmBtn = document.createElement('button');
  confirmBtn.className = 'modal-btn modal-btn--primary';
  confirmBtn.textContent = i18nService.getString('confirm') || 'Confirm';
  confirmBtn.addEventListener('click', () => {
    closeConfirmModal();
    if (onConfirm) onConfirm();
  });
  footer.appendChild(confirmBtn);
  
  panel.appendChild(footer);
  overlay.appendChild(panel);
  modalContainer.appendChild(overlay);
  
  // Focus confirm button
  setTimeout(() => {
    confirmBtn.focus();
  }, 50);
  
  // ESC key to cancel
  const handleEsc = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeConfirmModal();
      if (onCancel) onCancel();
      document.removeEventListener('keydown', handleEsc);
    }
  };
  document.addEventListener('keydown', handleEsc);
  (overlay as any).escHandler = handleEsc;
  
  // Click outside to cancel
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      closeConfirmModal();
      if (onCancel) onCancel();
    }
  });
}

/**
 * Close confirmation modal
 */
export function closeConfirmModal(): void {
  const modalContainer = document.getElementById('modal-container');
  if (!modalContainer) return;
  
  const overlay = modalContainer.querySelector('.modal-overlay');
  if (overlay && (overlay as any).escHandler) {
    document.removeEventListener('keydown', (overlay as any).escHandler);
  }
  
  modalContainer.innerHTML = '';
}

/**
 * Update footer with translated text
 */
export function updateFooter(): void {
  const footer = document.getElementById('ai-disclosure-footer');
  if (footer) {
    // Clear existing content
    footer.textContent = '';
    const p = document.createElement('p');
    p.textContent = i18nService.getString('aiGenerated') || 'Some audio and transcripts are AI-generated and human-reviewed.';
    footer.appendChild(p);
  }
}

/**
 * Debounce function to limit event handling
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function(this: any, ...args: Parameters<T>) {
    const context = this;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

/**
 * Format time in mm:ss format
 */
export function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds)) return '00:00';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
}
